<template>
  <div class="dash-layout">
    <!-- Header -->
    <header class="dash-header">
      <img src="/assets/images/CHECK-APP.png" alt="Check-App" class="dash-logo" />
      <span class="dash-welcome">{{ store.user?.first_name }} {{ store.user?.last_name }}</span>
      <button @click="handleLogout" class="btn-logout">
        <i class="fas fa-sign-out-alt mr-1"></i>
        <span class="btn-logout-text">Logout</span>
      </button>
    </header>

    <div class="dash-body">
      <!-- LEFT: structures list -->
      <aside class="dash-sidebar">
        <div class="sidebar-top">
          <h2 class="sidebar-title">{{ $t('myStructures') }}</h2>
          <button @click="startNew" class="btn-new" title="Register new structure">
            <i class="fas fa-plus"></i>
          </button>
        </div>

        <div v-if="store.isLoading" class="sidebar-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>

        <div v-else-if="uniqueStructures.length === 0" class="sidebar-empty">
          <i class="fas fa-building fa-2x"></i>
          <p>No structures yet.</p>
          <button @click="startNew" class="btn-new-full">
            <i class="fas fa-plus mr-1"></i> {{ $t('newStructure') }}
          </button>
        </div>

        <div v-else class="structure-list">
          <div
            v-for="s in uniqueStructures"
            :key="s.id"
            class="str-card"
            :class="{ active: activeStructure?.id === s.id }"
            @click="selectStructure(s)"
          >
            <div class="str-card-top">
              <div class="str-card-info">
                <span class="str-name">{{ s.name_en || s.name_fr }}</span>
                <span class="str-abbr">{{ s.abbreviation_en || s.abbreviation_fr }}</span>
              </div>
              <span :class="['status-pill', `s-${s.status}`]">{{ s.status }}</span>
            </div>
            <div class="str-progress-wrap">
              <div class="str-progress-bar" :style="{ width: completionPercent(s) + '%' }"></div>
            </div>
            <div class="str-progress-label">{{ completionPercent(s) }}% complete</div>
            <div v-if="s.status === 'rejected' && s.review_notes" class="str-review-note">
              <i class="fas fa-exclamation-triangle"></i> {{ s.review_notes }}
            </div>
          </div>
        </div>
      </aside>

      <!-- RIGHT: inline editor or welcome -->
      <main class="dash-main">
        <div v-if="!activeStructure && !isCreatingNew" class="dash-welcome-panel">
          <i class="fas fa-building welcome-icon"></i>
          <h2>Welcome, {{ store.user?.first_name }}!</h2>
          <p>Select a structure from the left to manage it, or register a new one.</p>
          <button @click="startNew" class="btn-start">
            <i class="fas fa-plus mr-2"></i> {{ $t('newStructure') }}
          </button>
        </div>
        <StructureEditor
          v-else
          :structure="activeStructure"
          @saved="onSaved"
          @cancel="cancelEdit"
        />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useRegistrationStore } from '@/stores/registration';
import StructureEditor from './StructureEditor.vue';

const store  = useRegistrationStore();
const router = useRouter();
const route  = useRoute();

const activeStructure = ref(null);
const isCreatingNew   = ref(false);

onMounted(() => {
  store.loadFromSession();
  if (!store.isLoggedIn) {
    router.replace(`/${route.params.lang}/register/login`);
    return;
  }
  store.fetchStructures();
});

// Show only one card per structure name — keep the most advanced status
const uniqueStructures = computed(() => {
  const map = new Map();
  const priority = { approved: 4, submitted: 3, revision_needed: 2, draft: 1, rejected: 0 };
  for (const s of store.structures) {
    const key = (s.name_en || s.name_fr || s.id).toLowerCase().trim();
    const existing = map.get(key);
    if (!existing) {
      map.set(key, s);
    } else {
      const sP = priority[s.status] ?? 0;
      const eP = priority[existing.status] ?? 0;
      if (sP > eP) map.set(key, s);
    }
  }
  return [...map.values()];
});

function completionPercent(s) {
  const checks = [
    s.name_en || s.name_fr,
    s.description_en || s.description_fr,
    s.state_order_id,
    s.email, s.phone,
    s.website_url, s.location,
    s.facebook_url || s.twitter_url || s.instagram_url || s.linkedin_url,
    s.photo_url,
    s.services?.length > 0,
    s.key_personnel?.length > 0,
    s.signed_document_url,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

function selectStructure(s) { activeStructure.value = s; isCreatingNew.value = false; }
function startNew() { activeStructure.value = null; isCreatingNew.value = true; }
function cancelEdit() { activeStructure.value = null; isCreatingNew.value = false; }

function onSaved() {
  store.fetchStructures().then(() => {
    if (activeStructure.value) {
      const updated = store.structures.find(s => s.id === activeStructure.value.id);
      if (updated) activeStructure.value = updated;
    } else {
      isCreatingNew.value = false;
    }
  });
}

function handleLogout() {
  store.logout();
  router.push(`/${route.params.lang}/register/login`);
}
</script>

<style scoped>
.dash-layout { display:flex; flex-direction:column; min-height:100vh; background:#f0f2f5; }
.dash-header { background:#1a3a5c; color:#fff; display:flex; align-items:center; gap:12px;
  padding:10px 16px; box-shadow:0 2px 8px rgba(0,0,0,.2); position:sticky; top:0; z-index:50; }
.dash-logo { height:22px; flex-shrink:0; }
.dash-welcome { flex:1; font-weight:600; font-size:.9rem; opacity:.9;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0; }
.btn-logout { background:rgba(255,255,255,.15); border:1px solid rgba(255,255,255,.3);
  color:#fff; border-radius:8px; padding:7px 12px; cursor:pointer; font-size:.875rem;
  transition:background .2s; display:flex; align-items:center; gap:4px; flex-shrink:0; }
.btn-logout:hover { background:rgba(255,255,255,.25); }
@media (max-width: 480px) {
  .dash-header { padding:8px 12px; gap:8px; }
  .dash-logo { height:18px; }
  .dash-welcome { font-size:.8rem; }
  .btn-logout-text { display:none; }
  .btn-logout { padding:7px 10px; }
}

.dash-body { display:flex; flex:1; overflow:hidden; }

.dash-sidebar { width:290px; flex-shrink:0; background:#fff; border-right:1px solid #e5e7eb;
  display:flex; flex-direction:column; overflow-y:auto; height:calc(100vh - 49px); position:sticky; top:49px; }
.sidebar-top { display:flex; align-items:center; justify-content:space-between;
  padding:16px; border-bottom:1px solid #e5e7eb; }
.sidebar-title { font-size:.95rem; font-weight:700; color:#1a3a5c; margin:0; }
.btn-new { background:#1a3a5c; color:#fff; border:none; border-radius:8px;
  width:30px; height:30px; display:flex; align-items:center; justify-content:center;
  cursor:pointer; transition:background .2s; }
.btn-new:hover { background:#2d6a9f; }
.sidebar-loading { display:flex; justify-content:center; padding:40px; color:#9ca3af; }
.sidebar-empty { text-align:center; padding:40px 16px; color:#9ca3af; }
.sidebar-empty i { margin-bottom:10px; }
.btn-new-full { background:#1a3a5c; color:#fff; border:none; border-radius:8px;
  padding:8px 16px; font-size:.85rem; cursor:pointer; margin-top:12px; }

.structure-list { padding:10px; display:flex; flex-direction:column; gap:8px; }
.str-card { background:#f9fafb; border:1.5px solid #e5e7eb; border-radius:12px;
  padding:14px; cursor:pointer; transition:all .2s; }
.str-card:hover { border-color:#93c5fd; background:#f0f5ff; }
.str-card.active { border-color:#1a3a5c; background:#eff6ff; box-shadow:0 0 0 2px #bfdbfe; }
.str-card-top { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:8px; }
.str-card-info { display:flex; flex-direction:column; gap:2px; }
.str-name { font-weight:700; color:#111827; font-size:.88rem; line-height:1.3; }
.str-abbr { font-size:.73rem; color:#9ca3af; }
.status-pill { border-radius:999px; padding:2px 9px; font-size:.68rem; font-weight:700;
  text-transform:uppercase; white-space:nowrap; }
.s-draft { background:#f3f4f6; color:#6b7280; }
.s-submitted { background:#eff6ff; color:#2563eb; }
.s-approved { background:#f0fdf4; color:#16a34a; }
.s-rejected { background:#fef2f2; color:#dc2626; }
.s-revision_needed { background:#fffbeb; color:#d97706; }
.str-progress-wrap { background:#e5e7eb; border-radius:999px; height:5px; margin-bottom:3px; overflow:hidden; }
.str-progress-bar { background:linear-gradient(90deg,#1a3a5c,#3b82f6); height:100%; border-radius:999px; transition:width .4s; }
.str-progress-label { font-size:.72rem; color:#9ca3af; }
.str-review-note { background:#fef9c3; border:1px solid #fde047; border-radius:6px;
  padding:6px 10px; font-size:.74rem; color:#854d0e; margin-top:8px; }

.dash-main { flex:1; overflow-y:auto; height:calc(100vh - 49px); }
.dash-welcome-panel { display:flex; flex-direction:column; align-items:center; justify-content:center;
  height:100%; text-align:center; color:#6b7280; padding:40px; }
.welcome-icon { font-size:3.5rem; color:#cbd5e1; margin-bottom:20px; }
.dash-welcome-panel h2 { font-size:1.4rem; font-weight:700; color:#1a3a5c; margin:0 0 8px; }
.dash-welcome-panel p { margin:0 0 24px; }
.btn-start { background:#1a3a5c; color:#fff; border:none; border-radius:10px;
  padding:12px 28px; font-size:.95rem; font-weight:600; cursor:pointer; transition:background .2s; }
.btn-start:hover { background:#2d6a9f; }

@media (max-width: 768px) {
  .dash-body { flex-direction:column; }
  .dash-sidebar { width:100%; height:auto; position:relative; top:0; max-height:260px; }
  .dash-main { height:auto; }
}
</style>